import {
  isPossiblePhoneNumber,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input";

/** Where a number with no country of its own is assumed to come from. */
const FALLBACK_COUNTRY: Country = "SE";

export type ApiPhoneNumberResult =
  | { readonly succeeded: true; readonly data: string }
  | { readonly succeeded: false };

/**
 * The form the API stores a phone number in: the country calling code followed
 * by the national number, with no leading plus.
 *
 * Phone numbers are held in E.164 everywhere else in the app, so this is the
 * last step before one is sent. A number that cannot be parsed has no such form.
 */
export const toApiPhoneNumber = (phoneNumber: string): ApiPhoneNumberResult => {
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  if (parsedPhoneNumber == null) return { succeeded: false };
  return {
    succeeded: true,
    data: `${parsedPhoneNumber.countryCallingCode}${parsedPhoneNumber.nationalNumber}`,
  };
};

/**
 * The E.164 value for a number the API gave us. Bare digits carry no sign that
 * they are international, so the plus goes back on before anything else reads
 * them.
 *
 * Numbers stored before the app settled on one format may be national instead,
 * with no country code at all, and a plus in front of those means nothing. They
 * are read as Swedish. The plus is tried first, so a stored number that already
 * carries a foreign country code keeps it rather than being claimed for Sweden.
 */
export const fromApiPhoneNumber = (
  phoneNumber: string | null | undefined,
): string => {
  const storedPhoneNumber = (phoneNumber ?? "").trim();
  if (storedPhoneNumber.length === 0) return "";
  if (storedPhoneNumber.startsWith("+")) return storedPhoneNumber;

  const internationalPhoneNumber = `+${storedPhoneNumber}`;
  if (isPossiblePhoneNumber(internationalPhoneNumber)) {
    return internationalPhoneNumber;
  }

  const nationalPhoneNumber = parsePhoneNumber(
    storedPhoneNumber,
    FALLBACK_COUNTRY,
  );
  return nationalPhoneNumber?.number ?? internationalPhoneNumber;
};
